/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { Dispatch, useState, SetStateAction } from 'react';
import {
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import { RichTextComponent } from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IBuyboxDataExtended } from './buybox.data';

import { IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { getAddressesAsync, getValidAddressAsync } from '../../../customProxy/retailExtension/DataActionExtension.g';
import { IFormattedAddressJson, IMunicipalAddress } from '../../../customProxy/retailExtension/DataServiceEntities.g';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { PropertyInformationSearchTypes, PropertyMaxLength, PropertyMinLength, PropertyType } from '../utilities/propertyTypeEnum';
import Input from './custom-components/input';
import isFormValidated from '../utilities/buybox/validate-property-info-form-fields';
import renderProductListOnAddress from '../utilities/buybox/render-product-list-on-address';

import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import addProductWithOrderAttributes, { IAllAddressesWithDocs } from '../utilities/buybox/add-product-with-order-attributes';
import GenericForm from '../utilities/buybox/generic-form';
import Spinner from './custom-components/spinner';
import onClickAddToCart from '../utilities/buybox/add-to-cart';
import LCSABForm from '../utilities/buybox/lcsab-form';
import TypeAheadInput, { IOption } from './custom-components/type-ahead-input';
import { IAllAddresses, ICartAndActions, IExtendedSimpleProduct, IFormState, ISearchAddressQuery } from '../utilities/types/buybox';
import SDABForm from '../utilities/buybox/sdab-form';
import addProductOnAddress from '../utilities/buybox/add-product-on-address';

// Function is to get default option for Identify Property by drop down
const selectDefaultValue = (showPropertyFieldOption: string) => {
    if (showPropertyFieldOption != '') {
        for (let index = 0; index < Object.keys(PropertyType).length / 2; index++) {
            if (
                showPropertyFieldOption
                    .trim()
                    .toLowerCase()
                    .includes(PropertyType[index].toLowerCase())
            ) {
                return index;
            }
        }
    }
    return 0;
};
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxProps<IBuyboxDataExtended>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        findInStore,
        price,
        title,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        context,
        data: {
            product: { result: product }
        }
    } = props;
    const [modalOpen, setModalOpen] = useState(false);
    const [genericModalOpen, setGenericModalOpen] = useState(false);
    const [sdabModalOpen, setSDABModalOpen] = useState(false);
    const [suite, setSuite] = useState<IFormState>({ value: '', errorMessage: '' });
    const [addressNumber, setAddressNumber] = useState<IFormState>({ value: '', errorMessage: '' });
    const [streetName, setStreetName] = useState<IFormState>({ value: '', errorMessage: '' });
    const [streetType, setStreetType] = useState<IFormState>({ value: '', errorMessage: '' });
    const [quadrant, setQuadrant] = useState<IFormState>({ value: '', errorMessage: '' });
    const [plan, setPlan] = useState<IFormState>({ value: '', errorMessage: '' });
    const [block, setBlock] = useState<IFormState>({ value: '', errorMessage: '' });
    const [lot, setLot] = useState<IFormState>({ value: '', errorMessage: '' });
    const [unit, setUnit] = useState<IFormState>({ value: '', errorMessage: '' });
    const [lookFor, setLookFor] = useState<IFormState>({ value: '', errorMessage: '' });
    const [ldcPlan, setLdcPlan] = useState<IFormState>({ value: '', errorMessage: '' });
    const [validated] = useState(false);
    const { showPropertyPA } = props.context.app.config;
    const [propertyType, setPropertyType] = useState(
        selectDefaultValue(props.data.productAttributes.result?.find(x => x.Name === showPropertyPA)?.TextValue || '')
    );

    const [allAddresses, setAllAddresses] = useState<IAllAddresses[]>([]);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [searchAddressQuery, setSearchAddressQuery] = useState<ISearchAddressQuery>({
        value: '',
        errorMessage: '',
        isAddressSelected: false
    });

    /* To remove the address from verified address list */
    const removeAddress = (index: number, productIndex: number, inputRef: React.MutableRefObject<HTMLInputElement | null>) => {
        allAddresses[index].relatedProduct?.splice(productIndex, 1);
        allAddresses[index].relatedProduct?.length === 0 && allAddresses.splice(index, 1);
        setAllAddresses([...allAddresses]);
        if (allAddresses.length === 0) {
            inputRef.current?.focus();
        }
    };
    /* When propety type dropdown value changes */
    const propertyOnChange = (value: string) => {
        setPropertyType(parseInt(value));
        setErrorMessages([]);
        clearValues();
    };
    /* Method will clear the value of input feild */
    const clearValues = () => {
        setSuite({ value: '', errorMessage: '' });
        setPlan({ value: '', errorMessage: '' });
        setLdcPlan({ value: '', errorMessage: '' });
        setUnit({ value: '', errorMessage: '' });
        setBlock({ value: '', errorMessage: '' });
        setLot({ value: '', errorMessage: '' });
        setAddressNumber({ value: '', errorMessage: '' });
        setStreetName({ value: '', errorMessage: '' });
        setStreetType({ value: '', errorMessage: '' });
        setQuadrant({ value: '', errorMessage: '' });
        setLookFor({ value: '', errorMessage: '' });
        setSearchAddressQuery({ value: '', errorMessage: '', isAddressSelected: false });
    };

    /* Method will add the product in to the cart */
    const _addToCart = async () => {
        setIsDisabled(true);
        const customAddress: IAllAddressesWithDocs[] = allAddresses.filter(
            address => address.relatedProduct?.filter(product => product.isChecked === true).length! > 0
        );
        await addProductWithOrderAttributes(customAddress, context);
        setModalOpen(false);
        setSDABModalOpen(false);
        clearValues();
        setErrorMessages([]);
        setAllAddresses([]);
        setIsDisabled(false);
    };

    const cartAndActions: ICartAndActions = {
        addToCart: addToCart,
        modalOpen,
        sdabModalOpen,
        plan,
        genericModalOpen,
        setGenericModalOpen,
        block,
        lot,
        unit,
        lookFor,
        propertyType,
        validated,
        ldcPlan,
        suite,
        addressNumber,
        streetName,
        setSearchAddressQuery,
        searchAddressQuery,
        streetType,
        quadrant,
        errorMessages,
        context,
        product,
        allAddresses,
        isDisabled,
        setIsDisabled,
        setAllAddresses,
        setSuite,
        setAddressNumber,
        setStreetName,
        setStreetType,
        setQuadrant,
        setLdcPlan,
        _addToCart,
        setModalOpen,
        setSDABModalOpen,
        setPlan,
        setBlock,
        setLot,
        setUnit,
        setLookFor,
        setPropertyType,
        removeAddress,
        setErrorMessages,
        propertyOnChange,
        clearValues
    };
    const richDescription = product?.Description;
    return (
        <Module {...ModuleProps}>
            <>
                <div className='buybox-left-col'>
                    {title}
                    <div className='buybox-product'>
                        <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
                        <RichTextComponent text={richDescription} className='buybox-description' />
                    </div>
                </div>
                <Node {...ProductInfoContainerProps}>
                    {price}
                    {_renderTextBlock(props.slots.textBlocks)}
                    {rating}
                    {keyInPrice && _renderKeyInPrice(keyInPrice)}
                    {inventoryLabel}
                    {_renderCartAndActions(cartAndActions, props)}
                    {findInStore && _renderFindInStore(findInStore)}
                    {_renderSocialShare(props.slots && props.slots?.socialShare)}
                    {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                </Node>
            </>
        </Module>
    );
};

/* Method will validate the input fields data and then 
 method will verify the address using CRT Proxy API. */
const _verifyAddress = async (
    cartAndAction: ICartAndActions,
    setIsDisabled: Dispatch<SetStateAction<boolean>>,
    props: React.PropsWithChildren<IBuyboxViewProps & IBuyboxProps<IBuyboxDataExtended>>,
    relatedProducts: IExtendedSimpleProduct[],
    buttonRef: React.MutableRefObject<HTMLButtonElement | null>,
    inputRef: React.MutableRefObject<HTMLInputElement | null>
) => {
    const { setErrorMessages } = cartAndAction;
    const selectedProducts = relatedProducts.filter(product => product.isChecked);
    if (!isFormValidated(cartAndAction, props)) {
        setIsDisabled(false);
        return;
    }
    const displayErrorMessages = (errorMessages: string[]) => {
        setIsDisabled(false);
        setErrorMessages(errorMessages);
        errorMessages.length === selectedProducts.length && !cartAndAction.allAddresses.length && inputRef.current?.focus();
    };
    setIsDisabled(true);
    setErrorMessages([]);
    const {
        data: { decryptionKeys }
    } = props;

    await addProductOnAddress(selectedProducts, cartAndAction, props.resources, decryptionKeys.result!, displayErrorMessages);
    setIsDisabled(false);
    buttonRef.current?.focus();
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }
    return <>{textBlocks[0]}</>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderCartAndActions = (
    cartAndAction: ICartAndActions,
    props: React.PropsWithChildren<IBuyboxViewProps & IBuyboxProps<IBuyboxDataExtended>>
): JSX.Element | undefined => {
    const [isAddressSelected, setIsAddressSelected] = React.useState(false);
    const { addToCart, addToWishlist, modalOpen, sdabModalOpen } = cartAndAction;
    const { genericModalOpen, setGenericModalOpen, plan, block, lot, unit } = cartAndAction;
    const { lookFor, validated, _addToCart, propertyType, ldcPlan } = cartAndAction;
    const { searchAddressQuery, setSearchAddressQuery, errorMessages } = cartAndAction;
    const { product, allAddresses, isDisabled } = cartAndAction;
    const { setIsDisabled, setAllAddresses, setSuite, setAddressNumber } = cartAndAction;
    const { setStreetName, setStreetType, setQuadrant, setModalOpen } = cartAndAction;
    const { setSDABModalOpen, setPlan, setBlock, setLot, setUnit, setLookFor } = cartAndAction;
    const { setLdcPlan, removeAddress, propertyOnChange, setErrorMessages } = cartAndAction;
    const { addressLabel, legalDescriptionLabel, legalDescriptionCondoLabel, rollNumberLabel, lincNumberLabel } = props.resources;
    const { titleNumberLabel, cpidLabel, selectItemsLabel, addToCartButtonLabel } = props.resources;
    const { invalidSearchAddressQueryErrorMessage, selectPropertiesLabel } = props.resources;
    const { closeModalLabel, productTypesLabel, listPriceLabel, propertyTypeSelectionLabel } = props.resources;
    const { identiyPropertyLabel, requiredText, planLabel, blockLabel, lotLabel, unitLabel } = props.resources;
    const { lookForLabel, noItemsSelectedLabel, loadingLabel, removeLabel, selectedItemsLabel } = props.resources;
    const { addNewItemLabel, errorPopUpHeaderLabel, internalServerError, serviceUnavialableErrorText } = props.resources;
    const { addressSearchInstructions } = props.resources;
    const [showLoader, setShowLoader] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const [relatedProducts, setRelatedProducts] = React.useState<IExtendedSimpleProduct[]>([]);
    const staticAddItemRef = React.useRef<HTMLButtonElement | null>(null);
    const addItemRef = React.useRef<HTMLButtonElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const { app, user, apiSettings } = props.context.request;
    const { clearValues } = cartAndAction;

    cartAndAction.clearValues = () => {
        setIsAddressSelected(false);
        clearValues();
    };

    React.useEffect(() => {
        isAddressSelected && addItemRef.current && addItemRef.current.focus();
    }, [isAddressSelected]);
    const { showPropertyPA } = props.context.app.config;
    let showPropertyFieldOption: string = props.data.productAttributes.result?.find(x => x.Name === showPropertyPA)?.TextValue || '';
    showPropertyFieldOption = showPropertyFieldOption.toLowerCase();
    if (!addToCart && !addToWishlist) {
        return undefined;
    }
    /* Method is use to update the check value in the state */
    const onProductClickChange = (addressIndex: number, productIndex: number) => {
        allAddresses[addressIndex].relatedProduct[productIndex].isChecked = !allAddresses[addressIndex].relatedProduct[productIndex]
            .isChecked;

        setAllAddresses([...allAddresses]);
    };
    /* Creating verified address list */
    const addressItems = allAddresses.map((address, index) => {
        switch (address.propertyType) {
            case PropertyType.address: {
                const { suite, number, streetName, streetType, quadrant } = address.addressVerified;
                const addressStr = `${suite} ${number} ${streetName} ${streetType} ${quadrant}`;
                return renderProductListOnAddress(
                    index,
                    address,
                    onProductClickChange,
                    removeAddress,
                    removeLabel,
                    addressLabel,
                    addressStr,
                    inputRef
                );
            }
            case PropertyType.legalDescription:
                const legalDesc = `${address.legalDescriptionVerified.plan} ${address.legalDescriptionVerified.block} ${address.legalDescriptionVerified.lot}`;
                return renderProductListOnAddress(
                    index,
                    address,
                    onProductClickChange,
                    removeAddress,
                    removeLabel,
                    legalDescriptionLabel,
                    legalDesc,
                    inputRef
                );
            case PropertyType.legalDescriptionCondo:
                const ldc = `${address.legalDescriptionCondoVerified.plan} ${address.legalDescriptionCondoVerified.unit}`;
                return renderProductListOnAddress(
                    index,
                    address,
                    onProductClickChange,
                    removeAddress,
                    removeLabel,
                    legalDescriptionCondoLabel,
                    ldc,
                    inputRef
                );
            case PropertyType.rollNumber:
                return renderProductListOnAddress(
                    index,
                    address,
                    onProductClickChange,
                    removeAddress,
                    removeLabel,
                    rollNumberLabel,
                    address.lookForVerified.lookFor!,
                    inputRef
                );
            case PropertyType.lincNumber:
                return renderProductListOnAddress(
                    index,
                    address,
                    onProductClickChange,
                    removeAddress,
                    removeLabel,
                    lincNumberLabel,
                    address.lookForVerified.lookFor!,
                    inputRef
                );
            case PropertyType.titleNumber:
                return renderProductListOnAddress(
                    index,
                    address,
                    onProductClickChange,
                    removeAddress,
                    removeLabel,
                    titleNumberLabel,
                    address.lookForVerified.lookFor!,
                    inputRef
                );
            case PropertyType.cpid:
                return renderProductListOnAddress(
                    index,
                    address,
                    onProductClickChange,
                    removeAddress,
                    removeLabel,
                    cpidLabel,
                    address.lookForVerified.lookFor!,
                    inputRef
                );
            default:
                return <li></li>;
        }
    });
    const setBodyStyle = (overflow: string, height: string) => {
        document.body.style.overflow = overflow;
        document.body.style.height = height;
    };
    const openModal = () => {
        setModalOpen(true);
        setBodyStyle('hidden', '100vh');
    };
    const closeModal = () => {
        setModalOpen(false);
        setBodyStyle('initial', 'unset');
        return;
    };
    const closeSWModal = () => {
        setGenericModalOpen(false);
        document.body.style.overflow = 'initial';
        document.body.style.height = 'unset';
        return;
    };

    const isShowPropertyInfoForm = () => {
        const count = Object.keys(PropertyInformationSearchTypes).length / 2;
        for (let i = 1; i <= count; i++) {
            if (showPropertyFieldOption.includes(PropertyInformationSearchTypes[i].toLowerCase())) {
                return true;
            }
        }
        return false;
    };
    const getAddtoCartButton = (
        onClickAction: any,
        checkGuestUserProduct: boolean,
        selectItemsLabel: string,
        isCartButtonDisabled?: boolean
    ) => (
        <>
            <button
                type='button'
                onClick={onClickAction}
                className='msc-add-to-cart'
                disabled={checkGuestUserProduct || isCartButtonDisabled}
            >
                {selectItemsLabel}
            </button>

            {checkGuestUserProduct && (
                <div className='cui alert-small information'>
                    <a href={props.context.request.user.signInUrl}>{props.resources.signInTxt}</a> {props.resources.signInToAddLabel}
                </div>
            )}
            {/* <button type='button' onClick={onClickAction} className='msc-add-to-cart' disabled={checkGuestUserProduct}>
                {checkGuestUserProduct ? props.resources.signInToAddLabel : selectItemsLabel}
            </button> */}
        </>
    );
    /* Method will display the 'select Items' button or 'Add TO Bag' button on the basis of hard codded product */
    const _renderAddToCartButton = () => {
        const { guestUserPA } = app.config;
        const checkGuestUserProduct = props.data.productAttributes.result?.find(x => x.Name === guestUserPA)?.BooleanValue
            ? false
            : !user.isAuthenticated;
        const isCartButtonDisabled = showPropertyFieldOption.includes(PropertyType[PropertyType.ptweb]);

        if (isShowPropertyInfoForm()) {
            return getAddtoCartButton(openModal, checkGuestUserProduct, selectItemsLabel, isCartButtonDisabled);
        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.sdab])) {
            return getAddtoCartButton(() => setSDABModalOpen(true), checkGuestUserProduct, selectItemsLabel, isCartButtonDisabled);
        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.lcsab])) {
            return getAddtoCartButton(() => setSDABModalOpen(true), checkGuestUserProduct, selectItemsLabel, isCartButtonDisabled);
        } else if (
            showPropertyFieldOption.includes(PropertyType[PropertyType.community]) ||
            showPropertyFieldOption.includes(PropertyType[PropertyType.quadrant]) ||
            showPropertyFieldOption.includes(PropertyType[PropertyType.blockProfile].toLowerCase()) ||
            showPropertyFieldOption.includes(PropertyType[PropertyType.ats].toLowerCase())
        ) {
            return getAddtoCartButton(() => setGenericModalOpen(true), checkGuestUserProduct, selectItemsLabel, isCartButtonDisabled);
        } else if (
            showPropertyFieldOption.includes(PropertyType[PropertyType.none]) ||
            showPropertyFieldOption.includes(PropertyType[PropertyType.cityWide].toLowerCase())
        ) {
            const propertyType =
                showPropertyFieldOption === PropertyType[PropertyType.none].toLowerCase() ? PropertyType.none : PropertyType.cityWide;
            return (
                <>
                    {getAddtoCartButton(
                        () => onClickAddToCart(product!, props, propertyType, setErrorMessage, setShowLoader),
                        checkGuestUserProduct,
                        props.resources.addToCartButtonLabel,
                        isCartButtonDisabled
                    )}
                    {errorMessage && (
                        <Modal
                            role='document'
                            aria-labelledby='productSearchTitleEr'
                            toggle={() => setErrorMessage('')}
                            isOpen={true}
                            className='cui'
                        >
                            <div className='modal-header'>
                                <h2 id='productSearchTitleEr' tabIndex={0} className='modal-title'>
                                    {errorPopUpHeaderLabel}
                                </h2>
                                <button className='btn-modal-close cui btn-md primary-text' onClick={() => setErrorMessage('')}>
                                    {closeModalLabel}
                                </button>
                            </div>

                            <ModalBody>
                                <span className='error-pop-msg'>{errorMessage}</span>
                            </ModalBody>
                        </Modal>
                    )}
                </>
            );
        } else {
            return checkGuestUserProduct || isCartButtonDisabled ? (
                getAddtoCartButton(() => {}, checkGuestUserProduct, props.resources.addToCartButtonLabel, isCartButtonDisabled)
            ) : (
                <div className='product-add-to-cart'>
                    <div className='buttons'>
                        {addToCart && addToCart.button}
                        {addToWishlist && addToWishlist.button}
                    </div>
                </div>
            );
        }
    };
    /* VSI-Customization -- START Get boolean value for disabled attribute of button  */
    const _setAddToCartButtonDisabledState = () => {
        let isItemCheckedFlag = true;

        for (const [_, address] of allAddresses.entries()) {
            if (address.relatedProduct?.some(x => x.isChecked)) {
                isItemCheckedFlag = false;
                break;
            }
        }
        return isItemCheckedFlag;
    };
    /* VSI-Customization -- END  */
    /* VSI-Customization -- START Get maxLength for different input fields  */
    const _getMaxLength = () => {
        switch (propertyType) {
            case PropertyType.rollNumber:
                return PropertyMaxLength.rollNumber;

            case PropertyType.lincNumber:
                return PropertyMaxLength.lincNumber;

            case PropertyType.cpid:
                return PropertyMaxLength.cpid;

            case PropertyType.titleNumber:
                return PropertyMaxLength.titleNumber;

            default:
                return PropertyMaxLength.default;
        }
    };
    const _getMinLength = () => {
        switch (propertyType) {
            case PropertyType.rollNumber:
                return PropertyMinLength.rollNumber;

            case PropertyType.lincNumber:
                return PropertyMinLength.lincNumber;

            case PropertyType.cpid:
                return PropertyMinLength.cpid;

            case PropertyType.titleNumber:
                return PropertyMinLength.titleNumber;

            default:
                return PropertyMinLength.default;
        }
    };
    /* VSI-Customization -- START Clear Address Related State/Hook values  */
    const clearOrFillAddressStateValues = (address?: IMunicipalAddress) => {
        if (address && Object.values(address).length > 0) {
            setAddressNumber({ value: `${address.HouseNumber}${address.HouseAlpha || ''}` });
            setStreetName({ value: address.StreetName! });
            setStreetType({ value: address.StreetType! });
            setQuadrant({ value: address.Quadrant! });
            setSuite({ value: `${address.SuiteNumber || ''}` });
            setIsAddressSelected(true);
        } else {
            setSuite({ value: '' });
            setAddressNumber({ value: '' });
            setStreetName({ value: '' });
            setStreetType({ value: '' });
            setQuadrant({ value: '' });
            setIsDisabled(false);
            setErrorMessages([]);
            setIsAddressSelected(false);
        }
    };
    /* VSI-Customization -- START Trigger verifyAddress function when user press Enter key after filling the input field  */
    const _handleVerifyAddress = () => {
        if (!(PropertyType.address === propertyType ? !isAddressSelected : isDisabled) || isProductsChecked()) {
            _verifyAddress(cartAndAction, setIsDisabled, props, relatedProducts, staticAddItemRef, inputRef);
        }
    };
    const getAddressOptions = async (query: string) => {
        let response: IFormattedAddressJson[] | null;
        let isServiceUnavailable = false;
        try {
            response = await getAddressesAsync({ callerContext: props.context.actionContext }, query.trim());
        } catch (error) {
            //@ts-ignore
            if (error?.status?.toLowerCase() === serviceUnavialableErrorText.toLowerCase()) {
                isServiceUnavailable = true;
            }
            response = null;
        }
        const addressOptions: IOption[] = [];
        if (response) {
            setSearchAddressQuery(prev => ({
                ...prev,
                errorMessage: '',
                isAddressSelected: false
            }));
            for (const formattedAddress of response) {
                const addr = formattedAddress.Address!;
                addressOptions.push({ name: addr, shortName: addr });
            }
        } else {
            const errorMessage = `${invalidSearchAddressQueryErrorMessage} ${query}`;
            setSearchAddressQuery(prev => ({
                ...prev,
                errorMessage: `${isServiceUnavailable ? internalServerError : errorMessage}`,
                isAddressSelected: false
            }));
        }
        return addressOptions;
    };
    /* VSI-Customization -- START Useeffect will run for each time when user type something in address searchbox and throttling implemented to prevent unnecessary calls (for each keyword) */

    React.useEffect(() => {
        const relatedProductIds = props.config.relatedProductsCollection.products.map(prod => prod.RecordId);
        getByIdsAsync({ callerContext: props.context.actionContext }, apiSettings.channelId, relatedProductIds, '').then(
            (result: SimpleProduct[]) => {
                const simpleProducts: IExtendedSimpleProduct[] = result.map(prod => ({ ...prod, isChecked: false }));
                const index = simpleProducts.findIndex(prod => prod.RecordId === product?.RecordId);
                simpleProducts[index] = { ...simpleProducts[index], isChecked: true };
                setRelatedProducts(simpleProducts);
            }
        );
    }, []);
    const handleAllRelatedProductsSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setRelatedProducts(prevState => {
                return prevState.map(prod => ({ ...prod, isChecked: true }));
            });
        } else {
            setRelatedProducts(prevState => {
                return prevState.map(prod => ({ ...prod, isChecked: false }));
            });
        }
    };

    const handleRelatedProductSelection = (product: IExtendedSimpleProduct) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newRelatedProducts = [...relatedProducts];
        const index = newRelatedProducts.findIndex(prod => prod.RecordId === product.RecordId);
        if (e.target.checked) {
            newRelatedProducts[index] = { ...product, isChecked: true };
        } else {
            newRelatedProducts[index] = { ...product, isChecked: false };
        }
        setRelatedProducts(newRelatedProducts);
    };

    /* VSI-Customization -- START When user selects the address option from dropdown update the address related hooks (suite, address, streetName, type, Quadrant) */
    const handleAddressSelection = async (option: IOption | null, shouldSwitchControl?: boolean) => {
        // Hit the API ValdiateAddress for valdiation of selected address
        if (option) {
            const validFormattedAddress: IMunicipalAddress[] = await getValidAddressAsync(
                { callerContext: props.context.actionContext },
                '',
                1,
                '',
                option.name
            );
            if (shouldSwitchControl) {
                addItemRef.current = staticAddItemRef.current;
            } else {
                addItemRef.current = null;
            }
            // Map the response object according to state/hooks and update the hooks accordingly

            setSearchAddressQuery(prev => {
                const isSame = prev.value === option.name;
                clearOrFillAddressStateValues(isSame ? validFormattedAddress[0] : undefined);
                return { ...prev, errorMessage: '', isAddressSelected: isSame };
            });
        } else {
            setSearchAddressQuery(prev => ({ ...prev, errorMessage: '', isAddressSelected: false }));
            clearOrFillAddressStateValues();
        }
    };
    // this function is to create a dropdown options
    const identifyPropertyOptions = () => {
        const showPropertyFieldOption: string = props.data.productAttributes.result?.find(x => x.Name === showPropertyPA)?.TextValue || '';
        let options: JSX.Element[] = [];
        if (showPropertyFieldOption != '') {
            for (let index = 0; index < Math.ceil(Object.keys(PropertyType).length / 2); index++) {
                if (
                    showPropertyFieldOption
                        .trim()
                        .toLowerCase()
                        .includes(PropertyType[index].toLowerCase())
                ) {
                    if (index != 7) {
                        //INC000000956250 Hide CPID
                        options?.push(<option value={index}>{props.resources[PropertyType[index] + 'Label']}</option>);
                    }
                }
            }
        }
        return options;
    };
    const isProductsChecked = () => relatedProducts.some(prod => prod.isChecked);
    const productPropertyType = () => {
        if (showPropertyFieldOption.includes(PropertyType[PropertyType.community])) {
            return PropertyType.community;
        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.quadrant])) {
            return PropertyType.quadrant;
        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.ats])) {
            return PropertyType.ats;
        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.blockProfile].toLowerCase())) {
            return PropertyType.blockProfile;
        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.sdab].toLowerCase())) {
            return PropertyType.sdab;
        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.lcsab].toLowerCase())) {
            return PropertyType.lcsab;
        }
        return 0;
    };
    const list = [PropertyType.rollNumber, PropertyType.lincNumber, PropertyType.titleNumber, PropertyType.cpid];
    const onChangeLookFor = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = propertyType === PropertyType.titleNumber ? e.target.value : e.target.value.replace(/[^0-9]/g, '');
        setLookFor({ value });
    };
    const onAddToCart = async () => {
        _addToCart && (await _addToCart());
        const newRelatedProducts = relatedProducts.map(prod => ({ ...prod, isChecked: false }));
        const current = newRelatedProducts.find(prod => prod.RecordId === product?.RecordId);
        current && (current.isChecked = true);
        setRelatedProducts(newRelatedProducts);
    };
    /* VSI-Customization -- END  */
    return (
        <div className='product-add-to-cart'>
            {/* VSI-Customization -- START Add to cart button  */}
            <div className='product-add-to-cart'>
                <div className='buttons'>
                    {_renderAddToCartButton()}
                    {showLoader && <Spinner text={loadingLabel} />}
                    {addToWishlist && addToWishlist.button}
                </div>
            </div>

            <div className='buttons'>
                {PropertyType.sdab === productPropertyType() && (
                    <SDABForm
                        isModelOpen={sdabModalOpen}
                        toggleSDABModal={setSDABModalOpen}
                        restProps={props}
                        updateAllAddresses={setAllAddresses}
                        addProductToCartWithAttribute={addProductWithOrderAttributes}
                        product={product!}
                    />
                )}
                {PropertyType.lcsab === productPropertyType() && (
                    <LCSABForm
                        isModelOpen={sdabModalOpen}
                        toggleSDABModal={setSDABModalOpen}
                        restProps={props}
                        updateAllAddresses={setAllAddresses}
                        addProductToCartWithAttribute={addProductWithOrderAttributes}
                        product={product!}
                    />
                )}
                <Modal
                    role='document'
                    aria-labelledby='productSearchTitle'
                    className='cui modal-lg'
                    isOpen={modalOpen}
                    toggle={closeModal}
                    keyboard={true}
                >
                    <div className='modal-header'>
                        <h2 id='productSearchTitle' tabIndex={0} className='modal-title'>
                            {selectPropertiesLabel}
                        </h2>
                        <button className='btn-modal-close cui btn-md primary-text' onClick={closeModal}>
                            {closeModalLabel}
                        </button>
                    </div>
                    <ModalBody>
                        <div>
                            <table>
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type='checkbox'
                                                checked={relatedProducts.every(prod => prod.isChecked)}
                                                name=''
                                                id=''
                                                aria-label='Select all items'
                                                onChange={handleAllRelatedProductsSelection}
                                            />
                                        </th>
                                        <th>{productTypesLabel}</th>
                                        <th>{listPriceLabel}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {relatedProducts.map(product => {
                                        return (
                                            <tr>
                                                <td>
                                                    <input
                                                        type='checkbox'
                                                        name=''
                                                        id=''
                                                        checked={product.isChecked}
                                                        onChange={handleRelatedProductSelection(product)}
                                                    />
                                                </td>
                                                <td>{product.Name}</td>
                                                <td>{props.context.cultureFormatter.formatCurrency(product.Price)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {!validated && (
                            <div className='property-search-wrap'>
                                <h3>{propertyTypeSelectionLabel}</h3>
                                <form onSubmit={e => e.preventDefault()}>
                                    <div className='form-group'>
                                        <label htmlFor='propertyType'>{identiyPropertyLabel}</label>
                                        <select
                                            name='propertyType'
                                            id='propertyType'
                                            defaultValue={propertyType.toString()}
                                            className='coc-dropdown__select'
                                            onChange={e => propertyOnChange(e.target.value)}
                                        >
                                            {identifyPropertyOptions()}
                                        </select>
                                    </div>

                                    {propertyType === PropertyType.address && (
                                        <>
                                            <TypeAheadInput
                                                label={
                                                    <label htmlFor='address-search-box'>
                                                        Search Address: <span className='staric'>{requiredText}</span>
                                                    </label>
                                                }
                                                minimumInput={4}
                                                name='address-search-box'
                                                errorID='addressSearchEr'
                                                getOptions={getAddressOptions}
                                                onOptionSelection={handleAddressSelection}
                                                query={searchAddressQuery.value}
                                                onQueryChange={(query: string) => {
                                                    clearOrFillAddressStateValues();
                                                    setSearchAddressQuery({
                                                        value: query,
                                                        errorMessage: '',
                                                        isAddressSelected: false
                                                    });
                                                }}
                                                errorMessage={searchAddressQuery.errorMessage}
                                                preventTab={!(isAddressSelected || allAddresses.length)}
                                                inputRef={inputRef}
                                            />
                                            <p className='address-search-instruction cui alert-small information'>
                                                {addressSearchInstructions}
                                            </p>
                                        </>
                                    )}
                                    {propertyType === PropertyType.legalDescription && (
                                        <div className='legal-des'>
                                            <div className='row'>
                                                <Input
                                                    label={planLabel}
                                                    name={'plan'}
                                                    errorID={'planEr'}
                                                    value={plan.value}
                                                    onChange={setPlan}
                                                    inputMaxLength={8}
                                                    isRequired={true}
                                                    errorMessage={plan.errorMessage}
                                                    requiredText={requiredText}
                                                />
                                                <Input
                                                    label={blockLabel}
                                                    name={'block'}
                                                    errorID={'blockEr'}
                                                    value={block.value}
                                                    onChange={setBlock}
                                                    inputMaxLength={4}
                                                />
                                                <Input
                                                    label={lotLabel}
                                                    name={'lot'}
                                                    errorID={'lotEr'}
                                                    value={lot.value}
                                                    onChange={setLot}
                                                    inputMaxLength={5}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {propertyType === PropertyType.legalDescriptionCondo && (
                                        <div className='legal-des-condo'>
                                            <div className='row'>
                                                <Input
                                                    label={planLabel}
                                                    name={'ldcPlan'}
                                                    errorID={'ldcPlanEr'}
                                                    value={ldcPlan.value}
                                                    onChange={setLdcPlan}
                                                    inputMaxLength={8}
                                                    isRequired={true}
                                                    errorMessage={ldcPlan.errorMessage}
                                                    requiredText={requiredText}
                                                />
                                                <Input
                                                    label={unitLabel}
                                                    name={'unit'}
                                                    errorID={'unitEr'}
                                                    value={unit.value}
                                                    onChange={setUnit}
                                                    inputMaxLength={4}
                                                    isRequired={true}
                                                    errorMessage={unit.errorMessage}
                                                    requiredText={requiredText}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {list.includes(propertyType) && (
                                        <div className='roll-number'>
                                            <div className='form-group'>
                                                <label htmlFor='lookFor'>
                                                    {lookForLabel}
                                                    <span className='staric'>{requiredText}</span>
                                                </label>
                                                <input
                                                    name='lookFor'
                                                    id='lookFor'
                                                    value={lookFor.value}
                                                    onChange={onChangeLookFor}
                                                    className='form-field'
                                                    type='text'
                                                    aria-describedby='lookForEr'
                                                    required
                                                    maxLength={_getMaxLength()}
                                                    minLength={_getMinLength()}
                                                />
                                                <div id='lookForEr' className='cui alert-small error'>
                                                    {lookFor.errorMessage}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className='btn-box'>
                                        <button
                                            type='button'
                                            disabled={
                                                (PropertyType.address === propertyType ? !isAddressSelected : isDisabled) ||
                                                !isProductsChecked()
                                            }
                                            className='cui utility-btn-solid'
                                            onClick={_handleVerifyAddress}
                                            ref={staticAddItemRef}
                                        >
                                            {addNewItemLabel}
                                        </button>
                                    </div>
                                    {errorMessages.map(errorMessage => (
                                        <span
                                            aria-live='assertive'
                                            aria-label={errorMessage}
                                            role='alert'
                                            className='msc-alert msc-alert-noborder msc-alert-danger'
                                        >
                                            <span className='msi-exclamation-triangle'></span>
                                            <span>
                                                <RichTextComponent text={errorMessage} />
                                            </span>
                                        </span>
                                    ))}
                                </form>
                                <div className='result-wrap'>
                                    <strong>
                                        {selectedItemsLabel}
                                        {addressItems && addressItems.length ? <span>{` (${addressItems.length}):`}</span> : ':'}
                                    </strong>
                                    {addressItems && addressItems.length ? null : <p>{noItemsSelectedLabel}</p>}
                                    <ul>{addressItems}</ul>
                                </div>
                            </div>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <div className='btn-box'>
                            <button
                                onClick={onAddToCart}
                                type='button'
                                className='cui btn-md primary'
                                disabled={_setAddToCartButtonDisabledState()}
                            >
                                <span className='btn-wrapper'>
                                    <span className='btn-text'>{addToCartButtonLabel}</span>
                                    <span className='cicon-angle-right right'></span>
                                </span>
                            </button>
                        </div>
                        {isDisabled ? <Spinner text={loadingLabel} /> : null}
                    </ModalFooter>
                </Modal>
                <GenericForm
                    isDCModalOpen={genericModalOpen}
                    closeDCModal={closeSWModal}
                    restProps={props}
                    propertyType={productPropertyType()}
                    setModalOpen={setGenericModalOpen}
                />
            </div>
        </div>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
